/**
 * Color extraction utility for dynamic gradient generation
 * Extracts dominant and contrasting colors from images
 */

// Convert RGB to HSL for better color analysis
const rgbToHsl = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
};

// Convert HSL back to RGB
const hslToRgb = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  if (s === 0) {
    return [l * 255, l * 255, l * 255]; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = hue2rgb(p, q, h + 1 / 3);
    const g = hue2rgb(p, q, h);
    const b = hue2rgb(p, q, h - 1 / 3);
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }
};

// Calculate color brightness
export const getBrightness = (r, g, b) => {
  return (r * 299 + g * 587 + b * 114) / 1000;
};

// Calculate contrast ratio between two colors
const getContrastRatio = (color1, color2) => {
  const brightness1 = getBrightness(color1.r, color1.g, color1.b);
  const brightness2 = getBrightness(color2.r, color2.g, color2.b);

  const lighter = Math.max(brightness1, brightness2);
  const darker = Math.min(brightness1, brightness2);

  return (lighter + 0.05) / (darker + 0.05);
};

// Extract dominant colors from image data
const extractDominantColors = (imageData, sampleSize = 1000) => {
  const data = imageData.data;
  const pixels = [];

  // Sample pixels (every nth pixel to improve performance)
  const step = Math.max(1, Math.floor(data.length / (sampleSize * 4)));

  for (let i = 0; i < data.length; i += step * 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Skip transparent pixels
    if (a < 128) continue;

    // Skip very dark or very light pixels (they don't make good gradients)
    const brightness = getBrightness(r, g, b);
    if (brightness < 20 || brightness > 235) continue;

    pixels.push({ r, g, b });
  }

  if (pixels.length === 0) {
    // Fallback colors if no suitable pixels found
    return [
      { r: 59, g: 130, b: 246 }, // Blue
      { r: 147, g: 51, b: 234 }  // Purple
    ];
  }

  // Simple k-means clustering to find dominant colors
  return performColorClustering(pixels, 8); // Get 8 clusters, then pick best 2
};

// Simple k-means clustering for color extraction
const performColorClustering = (pixels, k) => {
  if (pixels.length === 0) return [];

  // Initialize centroids randomly
  let centroids = [];
  for (let i = 0; i < k; i++) {
    const randomPixel = pixels[Math.floor(Math.random() * pixels.length)];
    centroids.push({ ...randomPixel });
  }

  // Perform k-means iterations
  for (let iteration = 0; iteration < 10; iteration++) {
    const clusters = Array(k).fill().map(() => []);

    // Assign pixels to nearest centroid
    pixels.forEach(pixel => {
      let minDistance = Infinity;
      let nearestCluster = 0;

      centroids.forEach((centroid, index) => {
        const distance = Math.sqrt(
          Math.pow(pixel.r - centroid.r, 2) +
          Math.pow(pixel.g - centroid.g, 2) +
          Math.pow(pixel.b - centroid.b, 2)
        );

        if (distance < minDistance) {
          minDistance = distance;
          nearestCluster = index;
        }
      });

      clusters[nearestCluster].push(pixel);
    });

    // Update centroids
    centroids = clusters.map(cluster => {
      if (cluster.length === 0) return centroids[0]; // Keep old centroid if cluster is empty

      const avgR = cluster.reduce((sum, p) => sum + p.r, 0) / cluster.length;
      const avgG = cluster.reduce((sum, p) => sum + p.g, 0) / cluster.length;
      const avgB = cluster.reduce((sum, p) => sum + p.b, 0) / cluster.length;

      return { r: Math.round(avgR), g: Math.round(avgG), b: Math.round(avgB) };
    });
  }

  // Sort centroids by cluster size (popularity)
  const clustersWithSizes = centroids.map((centroid, index) => ({
    color: centroid,
    size: pixels.filter(pixel => {
      const distances = centroids.map(c =>
        Math.sqrt(Math.pow(pixel.r - c.r, 2) + Math.pow(pixel.g - c.g, 2) + Math.pow(pixel.b - c.b, 2))
      );
      return distances.indexOf(Math.min(...distances)) === index;
    }).length
  }));

  return clustersWithSizes
    .sort((a, b) => b.size - a.size)
    .map(item => item.color);
};

// Apply brightness cap to ensure text readability
const capBrightness = (color, maxBrightness = 180) => {
  const brightness = getBrightness(color.r, color.g, color.b);
  if (brightness <= maxBrightness) return color;

  // Convert to HSL and reduce lightness to meet brightness requirement
  const [h, s, l] = rgbToHsl(color.r, color.g, color.b);
  const targetLightness = Math.min(l, 50); // Cap at 50% lightness
  const [r, g, b] = hslToRgb(h, s, targetLightness);

  return { r: Math.round(r), g: Math.round(g), b: Math.round(b) };
};

// Find two contrasting colors from dominant colors
const findContrastingColors = (dominantColors) => {
  if (dominantColors.length < 2) {
    // Fallback to default contrasting colors
    return [
      { r: 59, g: 130, b: 246 }, // Blue
      { r: 147, g: 51, b: 234 }  // Purple
    ];
  }

  let bestPair = [dominantColors[0], dominantColors[1]];
  let bestContrast = getContrastRatio(dominantColors[0], dominantColors[1]);

  // Try different combinations to find the most contrasting pair
  for (let i = 0; i < Math.min(dominantColors.length, 6); i++) {
    for (let j = i + 1; j < Math.min(dominantColors.length, 6); j++) {
      const contrast = getContrastRatio(dominantColors[i], dominantColors[j]);

      if (contrast > bestContrast) {
        bestContrast = contrast;
        bestPair = [dominantColors[i], dominantColors[j]];
      }
    }
  }

  // Ensure minimum contrast - if not good enough, enhance the colors
  if (bestContrast < 2.5) {
    bestPair = enhanceContrast(bestPair[0], bestPair[1]);
  }

  // Apply brightness cap to final colors for text readability
  bestPair = bestPair.map(color => capBrightness(color));

  return bestPair;
};

// Enhance contrast between two colors with brightness limit for text readability
const enhanceContrast = (color1, color2) => {
  const [h1, s1, l1] = rgbToHsl(color1.r, color1.g, color1.b);
  const [h2, s2, l2] = rgbToHsl(color2.r, color2.g, color2.b);

  // Make one color lighter and one darker, but cap lightness to maintain text readability
  const lightColor = l1 > l2 ?
    { h: h1, s: Math.min(s1 + 20, 100), l: Math.min(l1 + 30, 50) } :
    { h: h2, s: Math.min(s2 + 20, 100), l: Math.min(l2 + 30, 50) };

  const darkColor = l1 <= l2 ?
    { h: h1, s: Math.min(s1 + 20, 100), l: Math.max(l1 - 30, 15) } :
    { h: h2, s: Math.min(s2 + 20, 100), l: Math.max(l2 - 30, 15) };

  const [r1, g1, b1] = hslToRgb(lightColor.h, lightColor.s, lightColor.l);
  const [r2, g2, b2] = hslToRgb(darkColor.h, darkColor.s, darkColor.l);

  return [
    { r: r1, g: g1, b: b1 },
    { r: r2, g: g2, b: b2 }
  ];
};

// Main function to extract contrasting colors from an image URL
export const extractColorsFromImage = async (imageUrl) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Handle CORS

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Resize image for faster processing (max 200x200)
        const maxSize = 200;
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const dominantColors = extractDominantColors(imageData);
        const contrastingColors = findContrastingColors(dominantColors);

        resolve(contrastingColors);
      } catch (error) {
        console.warn('🎨 Failed to extract colors from image:', error);
        // Fallback to default colors
        resolve([
          { r: 59, g: 130, b: 246 }, // Blue
          { r: 147, g: 51, b: 234 }  // Purple
        ]);
      }
    };

    img.onerror = () => {
      console.warn('🎨 Failed to load image for color extraction:', imageUrl);
      // Fallback to default colors
      resolve([
        { r: 59, g: 130, b: 246 }, // Blue
        { r: 147, g: 51, b: 234 }  // Purple
      ]);
    };

    img.src = imageUrl;
  });
};

// Helper function to convert color object to CSS rgb string
export const colorToRgb = (color) => {
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
};

// Helper function to convert color object to CSS rgba string with opacity
export const colorToRgba = (color, opacity) => {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`;
};

// Generate 7 colors for animated background from 2 dominant colors
export const generateAnimatedBackgroundColors = (dominantColors) => {
  if (!dominantColors || dominantColors.length < 2) {
    // Return default colors if extraction failed
    return {
      gradientColors: ['#486fcb', '#7ea6df', '#f5a0b9', '#fbb164', '#f15050'],
      blobColors: ['#e51e25', '#e51e25']
    };
  }

  const [color1, color2] = dominantColors;

  // Convert to HSL for easier color manipulation
  const [h1, s1, l1] = rgbToHsl(color1.r, color1.g, color1.b);
  const [h2, s2, l2] = rgbToHsl(color2.r, color2.g, color2.b);

  // Generate 5 gradient colors by interpolating and creating variations
  const gradientColors = [];

  // Start with first color
  gradientColors.push(colorToRgb(color1));

  // Create lighter version of first color
  const [r1Light, g1Light, b1Light] = hslToRgb(h1, Math.max(s1 - 20, 20), Math.min(l1 + 20, 80));
  gradientColors.push(`rgb(${r1Light}, ${g1Light}, ${b1Light})`);

  // Create a blend between the two colors
  const avgH = (h1 + h2) / 2;
  const avgS = (s1 + s2) / 2;
  const avgL = (l1 + l2) / 2;
  const [rBlend, gBlend, bBlend] = hslToRgb(avgH, avgS, avgL);
  gradientColors.push(`rgb(${rBlend}, ${gBlend}, ${bBlend})`);

  // Create lighter version of second color
  const [r2Light, g2Light, b2Light] = hslToRgb(h2, Math.max(s2 - 20, 20), Math.min(l2 + 20, 80));
  gradientColors.push(`rgb(${r2Light}, ${g2Light}, ${b2Light})`);

  // End with second color
  gradientColors.push(colorToRgb(color2));

  // For blob colors, create saturated versions of the dominant colors
  const [r1Sat, g1Sat, b1Sat] = hslToRgb(h1, Math.min(s1 + 30, 100), Math.max(l1 - 10, 30));
  const [r2Sat, g2Sat, b2Sat] = hslToRgb(h2, Math.min(s2 + 30, 100), Math.max(l2 - 10, 30));

  const blobColors = [
    `rgb(${r1Sat}, ${g1Sat}, ${b1Sat})`,
    `rgb(${r2Sat}, ${g2Sat}, ${b2Sat})`
  ];

  return {
    gradientColors,
    blobColors
  };
}; 